import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Calculator, AlertTriangle, RotateCcw, TrendingDown, TrendingUp, Coins } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { AdSlot } from "@/components/AdSlot";

const DEFAULTS = {
  bankroll: 500,
  base_unit_pct: 1,
  multiplier: 2,
  stop_loss_pct: 40,
  stop_win_pct: 20,
  max_steps: 8,
};

export default function QBranch() {
  const [cfg, setCfg] = useState(DEFAULTS);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  useSeo({
    title: "Q Branch — Baccarat Staking Calculator | Licence to Play",
    description:
      "Free baccarat staking calculator. Enter your bankroll and get a negative-progression staking ladder with stop-loss and stop-win targets and risk warnings.",
    keywords: "baccarat calculator, staking calculator, bankroll calculator, negative progression",
  });

  const update = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const compute = async (e) => {
    e?.preventDefault();
    if (!cfg.bankroll || cfg.bankroll <= 0) {
      toast.error("Enter a bankroll greater than zero.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        bankroll: Number(cfg.bankroll),
        base_unit_pct: Number(cfg.base_unit_pct),
        multiplier: Number(cfg.multiplier),
        stop_loss_pct: Number(cfg.stop_loss_pct),
        stop_win_pct: Number(cfg.stop_win_pct),
        max_steps: Number(cfg.max_steps),
      };
      const r = await api.post("/calculator/plan", payload);
      setPlan(r.data);
    } catch {
      toast.error("Calculation failed. Check your inputs.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setCfg(DEFAULTS);
    setPlan(null);
  };

  const fmt = (n) => n.toLocaleString(undefined, { maximumFractionDigits: 2 });

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14">
      <div className="flex items-center gap-3 mb-3">
        <span className="overline text-gold">▸ Q Branch // Equipment</span>
      </div>
      <h1 className="font-display text-4xl sm:text-5xl text-white tracking-tighter">Staking Calculator</h1>
      <p className="mt-4 text-white/60 max-w-2xl leading-relaxed">
        Configure your bankroll and progression, then generate a full staking ladder with stop-loss and
        stop-win targets. Negative progression: stakes rise after a loss, reset on a win.
      </p>

      <div className="mt-10 grid lg:grid-cols-[380px_1fr] gap-6">
        {/* Control panel */}
        <form
          onSubmit={compute}
          data-testid="calculator-form"
          className="border border-white/10 bg-card p-6 h-fit"
        >
          <div className="flex items-center justify-between mb-6">
            <span className="overline text-gold">Input Parameters</span>
            <button type="button" onClick={reset} data-testid="calculator-reset" className="text-white/40 hover:text-gold transition-colors">
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>

          <Field label="Bankroll (£)" mono>
            <input
              data-testid="input-bankroll"
              type="number"
              value={cfg.bankroll}
              onChange={(e) => update("bankroll", e.target.value)}
              className={inputCls}
              min="1"
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Base unit %">
              <input data-testid="input-base-unit" type="number" step="0.5" value={cfg.base_unit_pct} onChange={(e) => update("base_unit_pct", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Loss multiplier">
              <input data-testid="input-multiplier" type="number" step="0.1" value={cfg.multiplier} onChange={(e) => update("multiplier", e.target.value)} className={inputCls} />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Stop-loss %">
              <input data-testid="input-stop-loss" type="number" value={cfg.stop_loss_pct} onChange={(e) => update("stop_loss_pct", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Stop-win %">
              <input data-testid="input-stop-win" type="number" value={cfg.stop_win_pct} onChange={(e) => update("stop_win_pct", e.target.value)} className={inputCls} />
            </Field>
          </div>

          <Field label={`Max progression steps: ${cfg.max_steps}`}>
            <input data-testid="input-max-steps" type="range" min="1" max="15" value={cfg.max_steps} onChange={(e) => update("max_steps", e.target.value)} className="w-full accent-gold" />
          </Field>

          <button
            type="submit"
            data-testid="calculator-submit-btn"
            disabled={loading}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-gold text-black font-semibold text-sm border border-gold hover:bg-transparent hover:text-gold transition-colors disabled:opacity-60"
          >
            <Calculator className="w-4 h-4" /> {loading ? "Computing…" : "Generate Plan"}
          </button>
        </form>

        {/* Output */}
        <div className="space-y-6">
          {!plan ? (
            <div className="border border-dashed border-white/15 bg-white/[0.01] p-14 text-center" data-testid="calculator-empty">
              <Coins className="w-10 h-10 text-white/20 mx-auto" />
              <p className="mt-4 font-mono text-sm text-white/40">
                Configure your parameters and generate a staking plan.
              </p>
            </div>
          ) : (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="calculator-results">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 border border-white/10">
                <Stat label="Base unit" value={`£${fmt(plan.base_unit)}`} icon={<Coins className="w-4 h-4" />} />
                <Stat label="Stop-loss" value={`£${fmt(plan.stop_loss)}`} tone="loss" icon={<TrendingDown className="w-4 h-4" />} />
                <Stat label="Stop-win" value={`£${fmt(plan.stop_win)}`} tone="win" icon={<TrendingUp className="w-4 h-4" />} />
                <Stat label="Max exposure" value={`£${fmt(plan.max_exposure)}`} icon={<AlertTriangle className="w-4 h-4" />} />
              </div>

              <div className="mt-6 border border-white/10 bg-card">
                <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
                  <span className="overline text-gold">Progression Ladder</span>
                  <span className="overline text-white/40">{plan.max_reached_steps} steps</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full font-mono text-sm" data-testid="ladder-table">
                    <thead>
                      <tr className="text-left text-white/40 border-b border-white/10">
                        <th className="px-5 py-3 font-medium">Step</th>
                        <th className="px-5 py-3 font-medium">Stake</th>
                        <th className="px-5 py-3 font-medium">Cumulative risk</th>
                        <th className="px-5 py-3 font-medium">Bankroll left</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plan.ladder.map((row) => (
                        <tr key={row.step} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                          <td className="px-5 py-3 text-gold">#{row.step}</td>
                          <td className="px-5 py-3 text-white">£{fmt(row.stake)}</td>
                          <td className="px-5 py-3 text-crimson-bright">£{fmt(row.cumulative_risk)}</td>
                          <td className="px-5 py-3 text-white/60">£{fmt(row.remaining_bankroll)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-6 border border-crimson-bright/30 bg-crimson/10 p-5" data-testid="calculator-warnings">
                <div className="flex items-center gap-2 text-crimson-bright mb-3">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="overline">Risk Warnings</span>
                </div>
                <ul className="space-y-2">
                  {plan.warnings.map((w, i) => (
                    <li key={i} className="text-sm text-white/70 leading-relaxed flex gap-2">
                      <span className="text-crimson-bright">▸</span> {w}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          )}

          <AdSlot size="728x90" label="Below Tool" />
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full bg-black/50 border border-white/20 text-white font-mono px-3 py-2.5 focus:border-gold focus:ring-1 focus:ring-gold/50 outline-none transition-colors";

const Field = ({ label, children, mono }) => (
  <div className="mb-4">
    <label className={`block overline text-white/50 mb-2 ${mono ? "" : ""}`}>{label}</label>
    {children}
  </div>
);

const Stat = ({ label, value, icon, tone }) => (
  <div className="bg-background px-5 py-5">
    <div className={`flex items-center gap-1.5 ${tone === "loss" ? "text-crimson-bright" : tone === "win" ? "text-emerald-bright" : "text-gold"}`}>
      {icon}
      <span className="overline text-white/40">{label}</span>
    </div>
    <div className="mt-2 font-mono text-xl text-white">{value}</div>
  </div>
);
